import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { MapPin, Mail, Phone, Clock, Navigation, Plus, Trash2 } from "lucide-react";

const LAYOUTS = [
  { key: "vertical", label: "Vertical", hint: "Info above map" },
  { key: "horizontal", label: "Horizontal", hint: "Info beside map" },
  { key: "stacked", label: "Stacked", hint: "Map above info" },
  { key: "list", label: "List", hint: "Compact, no map" },
];

const DEFAULTS = {
  enabled: true, heading: "Our Location", blurb: "", layout: "vertical",
  show_map: true, map_mode: "auto", map_embed_url: "", directions_note: "", contacts: [],
};

export function LocationWidgetEditor({ value, onChange, tenant }) {
  const cfg = { ...DEFAULTS, ...(value || {}) };
  const set = (patch) => onChange({ ...cfg, ...patch });
  const contacts = cfg.contacts || [];
  const setContact = (i, patch) => set({ contacts: contacts.map((c, j) => (j === i ? { ...c, ...patch } : c)) });
  const addContact = () => set({ contacts: [...contacts, { kind: "phone", label: "", value: "" }] });
  const removeContact = (i) => set({ contacts: contacts.filter((_, j) => j !== i) });

  return (
    <div className="space-y-6" data-testid="location-widget-editor">
      <div className="flex items-center justify-between">
        <div>
          <Label className="text-sm font-semibold">Show the location widget</Label>
          <p className="text-xs text-muted-foreground mt-0.5">Turn off to hide it everywhere it's embedded.</p>
        </div>
        <Switch checked={cfg.enabled !== false} onCheckedChange={(v) => set({ enabled: v })} data-testid="loc-enabled-switch" />
      </div>

      <div className="flex items-center justify-between border-t border-border pt-4">
        <div>
          <Label className="text-sm font-semibold">Feature main shop at top of menu</Label>
          <p className="text-xs text-muted-foreground mt-0.5">Show an ad-style card for your main shop above the menu with its live open/closed status.</p>
        </div>
        <Switch checked={cfg.feature_primary === true} onCheckedChange={(v) => set({ feature_primary: v })} data-testid="loc-feature-primary-switch" />
      </div>

      <div className="grid sm:grid-cols-2 gap-3">
        <div><Label>Heading</Label><Input className="rounded-none mt-1" value={cfg.heading} onChange={(e) => set({ heading: e.target.value })} placeholder="Our Location" data-testid="loc-heading-input" /></div>
        <div><Label>Subtitle <span className="text-muted-foreground normal-case">(optional)</span></Label><Input className="rounded-none mt-1" value={cfg.blurb} onChange={(e) => set({ blurb: e.target.value })} placeholder="Visit us in Tonopah" data-testid="loc-blurb-input" /></div>
      </div>

      <div>
        <Label>Layout</Label>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-1">
          {LAYOUTS.map((l) => (
            <button key={l.key} type="button" onClick={() => set({ layout: l.key })} data-testid={`loc-layout-${l.key}`}
              className={`border p-3 text-left transition-colors ${cfg.layout === l.key ? "border-primary bg-primary/5" : "border-border hover:border-primary/50"}`}>
              <LayoutGlyph type={l.key} active={cfg.layout === l.key} />
              <div className="text-sm font-medium mt-2">{l.label}</div>
              <div className="text-[11px] text-muted-foreground">{l.hint}</div>
            </button>
          ))}
        </div>
        <p className="text-xs text-muted-foreground mt-1.5">Each embed can override this with <code className="font-mono text-[11px]">data-morrcart-location-layout="…"</code>.</p>
      </div>

      <div className="border-t border-border pt-4">
        <div className="flex items-center gap-2 mb-1"><Phone className="h-4 w-4 text-primary" /><Label className="text-sm font-semibold">Contact lines</Label></div>
        <p className="text-xs text-muted-foreground mb-3">Add a labelled phone or email for each purpose — e.g. "Orders", "Events", "Text us". Leave the label blank for a plain line.</p>
        <div className="space-y-2">
          {contacts.map((c, i) => (
            <div key={i} className="flex items-center gap-2" data-testid={`loc-contact-row-${i}`}>
              <select className="rounded-none border border-border h-9 px-2 text-sm bg-background" value={c.kind || "phone"} onChange={(e) => setContact(i, { kind: e.target.value })} data-testid={`loc-contact-kind-${i}`}>
                <option value="phone">Phone</option>
                <option value="email">Email</option>
              </select>
              <Input className="rounded-none w-28" value={c.label || ""} onChange={(e) => setContact(i, { label: e.target.value })} placeholder="Label" data-testid={`loc-contact-label-${i}`} />
              <Input className="rounded-none flex-1" value={c.value || ""} onChange={(e) => setContact(i, { value: e.target.value })} placeholder={c.kind === "email" ? "hello@shop.com" : "(415) 555-2671"} data-testid={`loc-contact-value-${i}`} />
              <Button type="button" variant="ghost" size="icon" className="rounded-none text-destructive shrink-0" onClick={() => removeContact(i)} data-testid={`loc-contact-remove-${i}`}><Trash2 className="h-4 w-4" /></Button>
            </div>
          ))}
        </div>
        <Button type="button" variant="outline" size="sm" className="rounded-none mt-2 gap-1.5" onClick={addContact} data-testid="loc-contact-add"><Plus className="h-4 w-4" /> Add contact line</Button>
        {!contacts.length && <p className="text-xs text-muted-foreground mt-2">No lines added — the widget will fall back to your business email and phone.</p>}
      </div>

      <div className="border-t border-border pt-4">
        <div className="flex items-center gap-2 mb-1"><Navigation className="h-4 w-4 text-primary" /><Label className="text-sm font-semibold">Directions note <span className="text-muted-foreground normal-case font-normal">(optional)</span></Label></div>
        <Textarea className="rounded-none mt-1" rows={2} value={cfg.directions_note} onChange={(e) => set({ directions_note: e.target.value })} placeholder="Located right off the Salome Highway. Look for our signature cart and outdoor seating." data-testid="loc-directions-input" />
      </div>

      <div className="border-t border-border pt-4">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2"><MapPin className="h-4 w-4 text-primary" /><Label className="text-sm font-semibold">Map</Label></div>
          <Switch checked={cfg.show_map !== false} onCheckedChange={(v) => set({ show_map: v })} data-testid="loc-showmap-switch" />
        </div>
        {cfg.show_map !== false && (
          <div className="space-y-3">
            <div className="flex gap-2">
              <button type="button" onClick={() => set({ map_mode: "auto" })} className={`border px-3 py-1.5 text-sm ${cfg.map_mode !== "custom" ? "border-primary bg-primary/5" : "border-border"}`} data-testid="loc-map-auto">Auto (from address)</button>
              <button type="button" onClick={() => set({ map_mode: "custom" })} className={`border px-3 py-1.5 text-sm ${cfg.map_mode === "custom" ? "border-primary bg-primary/5" : "border-border"}`} data-testid="loc-map-custom">Custom embed link</button>
            </div>
            {cfg.map_mode === "custom" ? (
              <div>
                <Input className="rounded-none" value={cfg.map_embed_url} onChange={(e) => set({ map_embed_url: e.target.value })} placeholder="https://www.google.com/maps/embed?pb=..." data-testid="loc-map-url-input" />
                <p className="text-xs text-muted-foreground mt-1">In Google Maps → Share → <b>Embed a map</b> → copy the <code className="font-mono text-[11px]">src="…"</code> URL and paste it here.</p>
              </div>
            ) : (
              <p className="text-xs text-muted-foreground">The map is generated from your <b>mailing address</b> ({tenant?.mailing_address ? <span className="font-mono">{tenant.mailing_address}</span> : "set it in Business info above"}) — no Google key needed.</p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function LayoutGlyph({ type, active }) {
  const c = active ? "bg-primary" : "bg-muted-foreground/30";
  const box = "rounded-[2px]";
  if (type === "vertical") return <div className="h-8 flex flex-col gap-1"><div className={`${c} ${box} h-2 w-full`} /><div className="bg-muted-foreground/20 rounded-[2px] h-4 w-full" /></div>;
  if (type === "stacked") return <div className="h-8 flex flex-col gap-1"><div className="bg-muted-foreground/20 rounded-[2px] h-4 w-full" /><div className={`${c} ${box} h-2 w-full`} /></div>;
  if (type === "horizontal") return <div className="h-8 flex gap-1"><div className={`${c} ${box} h-full w-1/3`} /><div className="bg-muted-foreground/20 rounded-[2px] h-full w-2/3" /></div>;
  return <div className="h-8 flex flex-col gap-1 justify-center"><div className={`${c} ${box} h-1.5 w-full`} /><div className={`${c} ${box} h-1.5 w-4/5`} /><div className={`${c} ${box} h-1.5 w-3/5`} /></div>;
}
